import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CurrencyDollarIcon, 
  DocumentArrowDownIcon, 
  CalendarIcon,
  CreditCardIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const FeesPage = () => {
  const [selectedSport, setSelectedSport] = useState('all');

  const sportFees = [
    {
      sport: 'Football',
      category: 'fall',
      participationFee: 150,
      equipmentFee: 75,
      uniformDeposit: 100,
      total: 325,
      description: 'Includes helmet, pads, and practice gear rental'
    },
    {
      sport: 'Basketball (Boys)',
      category: 'winter',
      participationFee: 125,
      equipmentFee: 25,
      uniformDeposit: 50,
      total: 200,
      description: 'Includes practice gear and game uniform'
    },
    {
      sport: 'Basketball (Girls)',
      category: 'winter',
      participationFee: 125,
      equipmentFee: 25,
      uniformDeposit: 50,
      total: 200,
      description: 'Includes practice gear and game uniform'
    },
    {
      sport: 'Soccer (Boys)',
      category: 'fall',
      participationFee: 100,
      equipmentFee: 30,
      uniformDeposit: 40,
      total: 170,
      description: 'Cleats and shin guards not included'
    },
    {
      sport: 'Soccer (Girls)',
      category: 'fall',
      participationFee: 100,
      equipmentFee: 30,
      uniformDeposit: 40,
      total: 170,
      description: 'Cleats and shin guards not included'
    },
    {
      sport: 'Track & Field',
      category: 'spring',
      participationFee: 75,
      equipmentFee: 20,
      uniformDeposit: 35,
      total: 130,
      description: 'Running spikes not included'
    },
    {
      sport: 'Baseball',
      category: 'spring',
      participationFee: 125,
      equipmentFee: 50,
      uniformDeposit: 60,
      total: 235,
      description: 'Personal glove and cleats required'
    },
    {
      sport: 'Softball',
      category: 'spring',
      participationFee: 125,
      equipmentFee: 50,
      uniformDeposit: 60,
      total: 235,
      description: 'Personal glove and cleats required'
    }
  ];

  const requiredForms = [
    {
      name: 'Athletic Participation Physical',
      description: 'Must be completed within 12 months of participation',
      deadline: 'Before first practice',
      downloadLink: '/forms/athletic-physical.pdf',
      required: true
    },
    {
      name: 'Emergency Medical Information',
      description: 'Contact information and medical history',
      deadline: 'Before first practice',
      downloadLink: '/forms/emergency-medical.pdf',
      required: true
    },
    {
      name: 'Athletic Code of Conduct Agreement',
      description: 'Student and parent signature required',
      deadline: 'Before first practice',
      downloadLink: '/forms/code-of-conduct.pdf',
      required: true
    },
    {
      name: 'Transportation Permission Form',
      description: 'Permission for school transportation to events',
      deadline: 'Before first away game',
      downloadLink: '/forms/transportation.pdf',
      required: true
    },
    {
      name: 'Concussion Awareness Form',
      description: 'Acknowledgment of concussion risks and protocols',
      deadline: 'Before first practice',
      downloadLink: '/forms/concussion-awareness.pdf',
      required: true
    },
    {
      name: 'Insurance Waiver',
      description: 'Proof of insurance or waiver of school coverage',
      deadline: 'Before first practice',
      downloadLink: '/forms/insurance-waiver.pdf',
      required: false
    }
  ];

  const paymentOptions = [
    {
      method: 'Online Payment',
      description: 'Pay securely online with credit/debit card',
      icon: CreditCardIcon,
      link: '/registration',
      preferred: true
    },
    {
      method: 'Check Payment',
      description: 'Make checks payable to "East End Prep Athletics"',
      icon: DocumentArrowDownIcon,
      details: 'Mail to: East End Prep Athletic Department, 123 School St, City, State 12345'
    },
    {
      method: 'Payment Plan',
      description: 'Split payments over 3 months (contact office)',
      icon: CalendarIcon,
      details: 'Available for fees over $200'
    }
  ];

  const filteredFees = selectedSport === 'all' 
    ? sportFees 
    : sportFees.filter(fee => fee.category === selectedSport);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="mb-4">
            <Link to="/family-resources" className="text-blue-200 hover:text-white">
              Family Resources
            </Link>
            <span className="mx-2 text-blue-200">/</span>
            <span className="text-white">Athletic Fees & Forms</span>
          </nav>
          <h1 className="text-4xl font-bold mb-4">
            Athletic Fees & Forms
          </h1>
          <p className="text-xl text-blue-100">
            Information about athletic participation fees, payment options, and required forms.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Important Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                Important Deadline Information
              </h3>
              <p className="text-yellow-700">
                All fees must be paid and required forms submitted before the first practice. 
                Students cannot participate until all requirements are met.
              </p>
            </div>
          </div>
        </div>

        {/* Athletic Fees Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center mb-6">
            <CurrencyDollarIcon className="w-8 h-8 text-primary mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">
              Athletic Participation Fees
            </h2>
          </div>

          {/* Season Filter */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Season:
            </label>
            <select
              value={selectedSport}
              onChange={(e) => setSelectedSport(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Sports</option>
              <option value="fall">Fall Sports</option>
              <option value="winter">Winter Sports</option>
              <option value="spring">Spring Sports</option>
            </select>
          </div>

          {/* Fees Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sport
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Participation Fee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Equipment Fee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uniform Deposit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredFees.map((fee, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{fee.sport}</div>
                        <div className="text-sm text-gray-500">{fee.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${fee.participationFee}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${fee.equipmentFee}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${fee.uniformDeposit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-primary">
                      ${fee.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Uniform deposits are refundable upon return of uniforms in good condition. 
              Equipment fees cover the cost of shared team equipment and facility maintenance.
            </p>
          </div>
        </div>

        {/* Payment Options */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Payment Options
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {paymentOptions.map((option, index) => {
              const IconComponent = option.icon;
              return (
                <div key={index} className={`border rounded-lg p-6 ${option.preferred ? 'border-primary bg-blue-50' : 'border-gray-200'}`}>
                  <div className="flex items-center mb-4">
                    <IconComponent className={`w-6 h-6 mr-3 ${option.preferred ? 'text-primary' : 'text-gray-600'}`} />
                    <h3 className={`font-semibold ${option.preferred ? 'text-primary' : 'text-gray-900'}`}>
                      {option.method}
                      {option.preferred && <span className="ml-2 text-xs bg-primary text-white px-2 py-1 rounded">Preferred</span>}
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-4">{option.description}</p>
                  {option.details && (
                    <p className="text-sm text-gray-500">{option.details}</p>
                  )}
                  {option.link && (
                    <Link to={option.link} className="btn-primary mt-4 inline-block">
                      Pay Now
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Required Forms */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Required Forms
          </h2>
          <div className="space-y-4">
            {requiredForms.map((form, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 mr-3">
                        {form.name}
                      </h3>
                      {form.required && (
                        <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-2">{form.description}</p>
                    <p className="text-sm text-gray-500">
                      <strong>Deadline:</strong> {form.deadline}
                    </p>
                  </div>
                  <a
                    href={form.downloadLink}
                    className="btn-secondary ml-4 flex items-center"
                    download
                  >
                    <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                    Download
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeesPage;